import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { usStateByName } from '@hero/hero-utils/address/states';
import { formatPhoneNumber } from '@hero/hero-utils/phone';
import Tooltip from '@hero/ui-kit/components/Tooltip';
import LockIcon from '@hero/ui-kit/icons/branding/LockIcon';
import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import HighlightedText from '../../components/HighlightedText';
import buildHeaderColumns from '../../components/SortBy/buildHeaderColumns';
import Table from '../../components/Table';
import AddressIcon from '../../components/icons/AddressIcon';
import MedicalCheckIcon from '../../components/icons/MedicalCheckIcon';
import EmailIcon from '../../components/icons/EmailIcon';
import NoteIcon from '../../components/icons/NoteIcon';
import PhoneIcon from '../../components/icons/PhoneIcon';
import ChatBubbleIcon from '../../components/icons/ChatBubbleIcon';
import UnreadSmsBadge from '../../components/icons/UnreadSmsBadge';
import useWSNewLeadListener from '../../hooks/realtime/useWSNewLeadListener';
import useProviderNames from '../../api/useProviderNames';
import { useUserStatusContext } from '../../context/UserStatus';
import { useOrgIds } from '../../hooks/useSearchOrgIds';
import {
    FORM_FILLS_LIST_KIND,
    writeFormFillsListSnapshot
} from './Details/v2/useFormFillsListNeighbors';
import Pill from './components/Pill';
import NoteModal from './components/modals/NoteModal';
import { FormFillColumnDef, FormFillColumnId } from './columns';
import * as Style from './style.module.scss';
import { LeadData, LeadDataResponse, LeadNotePayload, LeadQueryParams } from './types';
import getApprovalStatus from './utils/getApprovalStatus';
import formatDateTime, { formatDateTimeLong } from './utils/formatDateTime';
import { getMedicalCheckStatus } from './utils/medicalCheckStatus';

const discoveryResultLabels: Record<number, string> = {
    1: 'Eligible',
    2: 'Not eligible',
    3: 'Not found'
};

const EMPTY = '—';

/** Notes are free text; keep hover previews to a readable length. */
const NOTE_PREVIEW_LENGTH = 180;

const formatDate = (value?: string | null) => (value ? formatDateTime(value) : EMPTY);

const formatBoolean = (value?: boolean | null) => {
    if (value === null || value === undefined) return EMPTY;
    return value ? 'Yes' : 'No';
};

const truncateNote = (body: string) =>
    body.length > NOTE_PREVIEW_LENGTH ? `${body.slice(0, NOTE_PREVIEW_LENGTH)}…` : body;

interface CommPillProps {
    Icon: React.FC<{ size?: number; muted?: boolean }>;
    count: number;
    unread?: number;
    /** Singular channel noun used to build the hover title ("1 call", "3 calls"). */
    noun: string;
}

const CommPill: React.FC<CommPillProps> = ({ Icon, count, unread, noun }) => {
    const title = count === 0 ? `No ${noun}s` : `${count} ${noun}${count === 1 ? '' : 's'}`;

    return (
        <span
            className={Style.commPill}
            title={unread && unread > 0 ? `${title} · ${unread} unread` : title}
        >
            <Icon size={14} muted={count === 0} />
            <span
                className={`${Style.commPillCount} ${count === 0 ? Style.commPillCountEmpty : ''}`}
            >
                {count}
            </span>
            {unread && unread > 0 ? <UnreadSmsBadge count={unread} /> : null}
        </span>
    );
};

const CommGroup: React.FC<{ lead: LeadData }> = ({ lead }) => (
    <div className={Style.commGroup}>
        <CommPill
            Icon={ChatBubbleIcon}
            count={lead.sms_count ?? 0}
            unread={lead.unread_sms_count}
            noun="SMS message"
        />
        <CommPill Icon={PhoneIcon} count={lead.call_count ?? 0} noun="logged call" />
        <CommPill Icon={EmailIcon} count={lead.email_count ?? 0} noun="email" />
    </div>
);

const TextCell: React.FC<{ children: React.ReactNode; strong?: boolean }> = ({
    children,
    strong
}) => (
    <P noDefaultMargin size="small" strong={strong} className="ellipsis">
        {children}
    </P>
);

interface LeadRowProps {
    lead: LeadData;
    search?: string;
    listQuery: LeadQueryParams;
    onOpenNote: (lead: LeadData) => void;
    visibleColumns: FormFillColumnId[];
    gridColumns: string;
    providerNames: Record<string, string>;
}

const Lead: React.FC<LeadRowProps> = ({
    lead,
    search,
    listQuery,
    onOpenNote,
    visibleColumns,
    gridColumns,
    providerNames
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { activeOrgId, setActiveOrg } = useUserStatusContext();

    const approvalStatus = getApprovalStatus(lead.approval_status, lead.approval_sub_status);

    const isLocked = React.useMemo(() => {
        return lead.lock_status === 'locked';
    }, [lead.lock_status]);

    const handleNavigate = () => {
        const from = `${location.pathname}${location.search}`;
        const snapshot = { from, listQuery, listKind: FORM_FILLS_LIST_KIND };
        writeFormFillsListSnapshot(snapshot);
        // [HWM-23014] A cross-provider search can list rows from a provider that is not the
        // active one. The detail page requests under activeOrgId, so switch tenant first or it
        // 403s and useRequest ejects the user to Home.
        if (lead.hero_organization_id && lead.hero_organization_id !== activeOrgId) {
            setActiveOrg(lead.hero_organization_id);
        }
        navigate(`/form-fills/${lead.lead_id}`, { state: snapshot });
    };

    // HWM-22633 (round 2): Form Fill list "Location" column shows the
    // 2-letter state code (CA, NY, FL, …). The backend can ship either a
    // 2-letter code or a full state name — normalise here. Falls back to the
    // raw value if the name lookup fails (e.g. unknown / non-US state).
    const state =
        (lead.state?.length || 0) > 2 ? usStateByName(lead.state)?.code || lead.state : lead.state;
    const name = `${lead.first_name} ${lead.last_name}`;
    const phoneDisplay = lead.phone ? formatPhoneNumber(lead.phone) : null;
    const caregiverName = [lead.caregiver_first_name, lead.caregiver_last_name]
        .filter(Boolean)
        .join(' ');

    const cells: Record<FormFillColumnId, () => React.ReactNode> = {
        formFill: () => (
            /* Form Fill: name + email + phone, with the lock icon attached to the
               cell right after the contact block per Figma 5233:2772. */
            <div className={`${Style.formFillCell} ${isLocked ? Style.locked : ''}`}>
                <div className={Style.formFillText}>
                    <strong>
                        <HighlightedText text={name} highlight={search} />
                    </strong>
                    <P size="small" noDefaultMargin className="ellipsis">
                        {lead.email ? (
                            <HighlightedText highlight={search} text={lead.email} />
                        ) : (
                            <>
                                {`Contact: `}
                                <HighlightedText
                                    highlight={search}
                                    text={lead.caregiver_email || ''}
                                />
                            </>
                        )}
                    </P>
                    {phoneDisplay ? (
                        <P size="small" noDefaultMargin className="ellipsis">
                            {phoneDisplay}
                        </P>
                    ) : null}
                    {lead.external_user_id && (
                        <P strong size="small" noDefaultMargin>
                            <HighlightedText highlight={search} text={lead.external_user_id} />
                        </P>
                    )}
                </div>
                {isLocked ? (
                    <Tooltip
                        content={'Another agent is working with the patient.'}
                        placement="right"
                    >
                        <LockIcon size="utility" type="utility" className={Style.lockIcon} />
                    </Tooltip>
                ) : null}
            </div>
        ),
        // [HWM-23014] Which provider this form fill belongs to. Only rendered for multi-org
        // logins (the column is absent otherwise), so a cross-provider search result is
        // attributable. Falls back to the raw org id until the provider name resolves.
        provider: () => (
            <TextCell>
                {lead.hero_organization_id
                    ? // Same fallback shape as OrgSwitcher — never surface a bare org id as if it
                      // were a provider name.
                      providerNames[lead.hero_organization_id] ||
                      `Organization ${lead.hero_organization_id}`
                    : EMPTY}
            </TextCell>
        ),
        dateOfBirth: () => <TextCell>{formatDate(lead.date_of_birth)}</TextCell>,
        caregiver: () => (
            <TextCell>
                <HighlightedText highlight={search} text={caregiverName || EMPTY} />
            </TextCell>
        ),
        location: () => <div>{state || EMPTY}</div>,
        zip: () => <TextCell>{lead.zip || EMPTY}</TextCell>,
        medCheck: () => (
            <div className={Style.iconCell}>
                <MedicalCheckIcon status={getMedicalCheckStatus(lead)} />
            </div>
        ),
        address: () => (
            <div className={Style.iconCell}>
                <AddressIcon status={lead.address_validation_status} />
            </div>
        ),
        /* SLA text — placed before Communication per Figma 5233:2772. A postponed
           lead has a paused timer, so the postponement wins over `sla_text`
           (which the backend keeps emitting) and the reason moves to the hover. */
        sla: () => (
            <div>
                {lead.sla_postponed_until ? (
                    <Tooltip
                        content={
                            <>
                                {`Postponed until ${formatDateTimeLong(lead.sla_postponed_until)}`}
                                {lead.sla_postpone_reason ? (
                                    <>
                                        <br />
                                        {lead.sla_postpone_reason}
                                    </>
                                ) : null}
                            </>
                        }
                        placement="top-left"
                        width="26rem"
                    >
                        <p className={`${Style.slaCell} ${Style.slaPostponed}`}>Postponed</p>
                    </Tooltip>
                ) : (
                    <p
                        className={`${Style.slaCell} ${lead.sla_text?.toLowerCase().includes('overdue') ? Style.danger : ''}`}
                    >
                        {lead.sla_text || EMPTY}
                    </p>
                )}
            </div>
        ),
        communication: () => (
            <div>
                <CommGroup lead={lead} />
            </div>
        ),
        /* Status pill — single badge (no sub-status filter, but the Medical-check
           status renders a distinct pill for scheduled vs missed via
           approval_sub_status). */
        status: () => (
            <div className={Style.statusCell}>
                <Pill label={approvalStatus.label} className={approvalStatus.className} />
            </div>
        ),
        priority: () => (
            <div className={Style.iconCell}>
                {lead.priority_tier === undefined || lead.priority_tier === null
                    ? EMPTY
                    : `P${lead.priority_tier}`}
            </div>
        ),
        heroMember: () => <TextCell>{formatBoolean(lead.is_member)}</TextCell>,
        insuranceCarrier: () => <TextCell>{lead.insurance_carrier_by_user || EMPTY}</TextCell>,
        insurancePolicyId: () => <TextCell>{lead.insurance_policy_id || EMPTY}</TextCell>,
        insuranceDiscovery: () => (
            <TextCell>
                {lead.discovery_result
                    ? discoveryResultLabels[lead.discovery_result]
                    : formatBoolean(lead.has_insurance_discovery)}
            </TextCell>
        ),
        createdAt: () => <TextCell>{formatDate(lead.created_at)}</TextCell>,
        completedAt: () => <TextCell>{formatDate(lead.completed_at)}</TextCell>,
        updatedAt: () => <TextCell>{formatDate(lead.updated_at)}</TextCell>,
        statusChangedAt: () => <TextCell>{formatDate(lead.status_changed_timestamp)}</TextCell>,
        expiresAt: () => <TextCell>{formatDate(lead.expires_at)}</TextCell>,
        claimedBy: () => <TextCell>{lead.claimed_by || EMPTY}</TextCell>,
        exported: () => <TextCell>{formatBoolean(lead.is_exported)}</TextCell>,
        latestNote: () => (
            <TextCell>
                {lead.latest_note ? (
                    <span title={lead.latest_note.body}>{lead.latest_note.body}</span>
                ) : (
                    EMPTY
                )}
            </TextCell>
        ),
        nextAction: () => (
            <div>
                <P noDefaultMargin size="small" className={Style.nextActionText}>
                    {lead.next_action || EMPTY}
                </P>
            </div>
        )
    };

    return (
        <div
            tabIndex={0}
            role="button"
            className={`${Style.item} ${isLocked ? Style.locked : ''}`}
            onClick={handleNavigate}
            onKeyDown={() => null}
        >
            <Section noDefaultPadding className={`${Style.itemTopLevel}`}>
                <Container
                    gridTemplateColumns={gridColumns}
                    className={Style.itemTopLevelContainer}
                >
                    {visibleColumns.map((id) => (
                        <React.Fragment key={id}>{cells[id]()}</React.Fragment>
                    ))}

                    {/* Note icon — clickable; opens NoteModal for inline
                        add/edit/delete. stopPropagation prevents the parent
                        row click from navigating to lead detail. */}
                    <div
                        className={`${Style.iconCell} ${Style.iconCellClickable}`}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenNote(lead);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                e.preventDefault();
                                onOpenNote(lead);
                            }
                        }}
                        aria-label={lead.has_notes ? 'Edit sticky note' : 'Add sticky note'}
                    >
                        {lead.latest_note ? (
                            <Tooltip
                                content={
                                    <>
                                        {truncateNote(lead.latest_note.body)}
                                        <br />
                                        {`Updated ${formatDateTimeLong(lead.latest_note.updated_at)}`}
                                    </>
                                }
                                placement="top-right"
                                width="28rem"
                            >
                                <NoteIcon hasNotes />
                            </Tooltip>
                        ) : (
                            <NoteIcon hasNotes={!!lead.has_notes} />
                        )}
                    </div>
                </Container>
            </Section>
        </div>
    );
};

interface LeadsTableProps {
    data?: LeadDataResponse;
    isLoading?: boolean;
    onFetchLeads: () => void;
    search?: string;
    listQuery: LeadQueryParams;
    isColumnVisible: (id: FormFillColumnId) => boolean;
    gridColumns: string;
    /**
     * [HWM-23014] Column set for this login — carries Provider only when multi-org. Required, and
     * must be the same array `gridColumns` was built from: the grid tracks and the row cells are
     * positional, so two different column sets would silently misalign the table.
     */
    columns: FormFillColumnDef[];
}

const LeadsTable: React.FC<LeadsTableProps> = ({
    data,
    isLoading = false,
    onFetchLeads,
    search = '',
    listQuery,
    isColumnVisible,
    gridColumns,
    columns
}) => {
    useWSNewLeadListener(onFetchLeads);

    // [HWM-23014] Provider names for the attribution column. useProviderNames no-ops for
    // single-org logins, which is also when the Provider column is absent.
    const providerNames = useProviderNames(useOrgIds());

    // HWM-22633 round 3: single NoteModal instance switched per-row.
    // Holds the lead currently being noted so the modal opens prefilled
    // from `lead.latest_note` and routes Save → POST or PATCH accordingly.
    const [noteLead, setNoteLead] = React.useState<LeadData | null>(null);

    // HWM-22633 round 4: local override map so the orange-dot badge and
    // modal prefill reflect the latest save WITHOUT a full list refetch.
    // Keyed by `lead_id` (UUID). Overrides hold server-confirmed payloads,
    // so they're already canonical — let them persist for the component
    // lifetime. (Auto-resetting on every `data` identity change would race
    // a refetch triggered between save and modal close, briefly flicking
    // the row back to the pre-save state.) Overrides disappear naturally
    // when the user navigates away and the table unmounts.
    const [noteOverrides, setNoteOverrides] = React.useState<
        Record<string, LeadNotePayload | null>
    >({});

    const handlePatchListRow = React.useCallback(
        (leadId: string, latestNote: LeadNotePayload | null) => {
            setNoteOverrides((prev) => ({ ...prev, [leadId]: latestNote }));
        },
        []
    );

    const applyOverride = React.useCallback(
        (lead: LeadData): LeadData => {
            if (!(lead.lead_id in noteOverrides)) return lead;
            const override = noteOverrides[lead.lead_id];
            return { ...lead, latest_note: override, has_notes: !!override };
        },
        [noteOverrides]
    );

    const visible = React.useMemo(
        () => columns.filter((column) => isColumnVisible(column.id)),
        [columns, isColumnVisible]
    );

    const visibleColumns = React.useMemo(() => visible.map((column) => column.id), [visible]);

    const headerColumns = React.useMemo(
        () => [
            // Already filtered to the visible ones, so the builder's own filter is a no-op here.
            ...buildHeaderColumns(visible, () => true),
            <div key="note" />
        ],
        [visible]
    );

    return (
        <>
            <Table
                headerGridTemplateColumns={gridColumns}
                headerClassName={Style.tableHeader}
                headerColumns={headerColumns}
                pagerData={data}
                loading={isLoading}
            >
                {data?.leads.map((item) => {
                    const lead = applyOverride(item);
                    return (
                        <Lead
                            search={search}
                            key={item.id}
                            lead={lead}
                            onOpenNote={setNoteLead}
                            listQuery={listQuery}
                            visibleColumns={visibleColumns}
                            gridColumns={gridColumns}
                            providerNames={providerNames}
                        />
                    );
                })}
            </Table>
            {noteLead ? (
                <NoteModal
                    leadId={noteLead.lead_id}
                    existingNote={
                        (noteOverrides[noteLead.lead_id] ?? noteLead.latest_note)
                            ? {
                                  id: (noteOverrides[noteLead.lead_id] ?? noteLead.latest_note)!.id,
                                  body: (noteOverrides[noteLead.lead_id] ?? noteLead.latest_note)!
                                      .body
                              }
                            : null
                    }
                    onClose={() => setNoteLead(null)}
                    onPatchListRow={handlePatchListRow}
                />
            ) : null}
        </>
    );
};

export default LeadsTable;
